import React from 'react';
import { Link } from 'react-router-dom';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: 'how-to-convert-heic-to-jpg',
    title: 'How to Convert HEIC to JPG: Complete Guide (2025)',
    excerpt: 'Got iPhone photos you can\'t open on Windows or Android? Learn how to convert HEIC files to JPG in seconds, no software install required.',
    date: '2025-01-26',
    readTime: '5 min read',
    category: 'Tutorial'
  },
  {
    slug: 'why-cant-i-open-iphone-photos',
    title: 'Why Can\'t I Open iPhone Photos on Windows, Android, or PC?',
    excerpt: 'Getting "Can\'t open this file" errors with iPhone photos? Here\'s why it happens and how to fix it in 30 seconds.',
    date: '2025-01-26',
    readTime: '4 min read',
    category: 'Problem & Solution'
  },
  {
    slug: 'share-iphone-photos-with-android',
    title: 'How to Share iPhone Photos with Android Users (That Actually Work)',
    excerpt: 'Sending photos from iPhone to Android but they can\'t open them? Here are 5 foolproof methods that guarantee compatibility.',
    date: '2025-01-26',
    readTime: '6 min read',
    category: 'Guide'
  },
  {
    slug: 'best-webp-converter',
    title: 'Best Free WEBP Converters (2025): Ranked & Tested',
    excerpt: 'We tested 15+ WEBP converters to find the best. Compare speed, privacy, quality, and features. See why PixelShift ranks #1.',
    date: '2025-01-22',
    readTime: '8 min read',
    category: 'Review'
  },
  {
    slug: 'webp-to-png-converter',
    title: 'Best Free WEBP to PNG Converter Online',
    excerpt: 'Need to convert WEBP to PNG? Discover the fastest, most private converter with perfect quality preservation and zero limits.',
    date: '2025-01-22',
    readTime: '5 min read',
    category: 'Guide'
  },
  {
    slug: 'how-to-open-webp-files',
    title: 'How to Open WEBP Files: The Complete Guide',
    excerpt: 'Can\'t open WEBP files? Learn 4 methods to view and convert WEBP images on Windows, Mac, mobile, and in browsers.',
    date: '2025-01-22',
    readTime: '6 min read',
    category: 'Tutorial'
  },
  {
    slug: 'chrome-extension-webp-converter',
    title: 'WEBP Converter Chrome Extension: Convert with One Click',
    excerpt: 'Right-click any WEBP image and convert it to JPG or PNG instantly. Free Chrome extension with 100% private, client-side conversion.',
    date: '2025-01-22',
    readTime: '4 min read',
    category: 'Tools'
  },
  {
    slug: 'what-is-webp-and-why-cant-i-open-it',
    title: 'What is WEBP and Why Can\'t I Open These Files?',
    excerpt: 'Discover why WEBP files won\'t open on your computer and how to easily convert them to JPG or PNG format.',
    date: '2025-01-22',
    readTime: '5 min read',
    category: 'Guide'
  },
  {
    slug: 'how-to-convert-webp-to-jpg',
    title: 'How to Convert WEBP to JPG: 3 Easy Methods',
    excerpt: 'Complete guide to converting WEBP images to JPG format. Compare online tools, desktop software, and browser-based converters.',
    date: '2025-01-22',
    readTime: '7 min read',
    category: 'Tutorial'
  },
  {
    slug: 'webp-vs-jpg-which-format-to-use',
    title: 'WEBP vs JPG: Which Image Format Should You Use?',
    excerpt: 'Understanding the differences between WEBP and JPG formats. Learn when to use each for optimal web performance.',
    date: '2025-01-22',
    readTime: '6 min read',
    category: 'Comparison'
  }
];

const Blog: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            PixelShift Blog
          </h1>
          <p className="text-xl text-gray-400">
            Guides, tutorials, and tips for image conversion
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="space-y-8">
          {blogPosts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="block bg-gray-800 rounded-2xl p-6 md:p-8 hover:bg-gray-750 transition-all duration-200 border border-gray-700 hover:border-brand-primary"
            >
              <div className="flex items-center gap-4 mb-3">
                <span className="px-3 py-1 bg-brand-primary/20 text-brand-primary rounded-full text-sm font-medium">
                  {post.category}
                </span>
                <span className="text-gray-500 text-sm">{post.date}</span>
                <span className="text-gray-500 text-sm">•</span>
                <span className="text-gray-500 text-sm">{post.readTime}</span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 hover:text-brand-primary transition-colors">
                {post.title}
              </h2>
              
              <p className="text-gray-400 text-lg">
                {post.excerpt}
              </p>
              
              <div className="mt-4 flex items-center text-brand-primary font-medium">
                Read more
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">
            Ready to Convert Your Images?
          </h3>
          <p className="text-white/90 mb-6">
            Try PixelShift - convert WEBP & HEIC (iPhone photos) instantly
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-brand-primary font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-all duration-200"
          >
            Convert Now - Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Blog;

